import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Media,
  MediaPickerComponent,
  PickerMode,
  useUpdateAsset,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes';
import LoadingIndicator from '../loadingIndicator';
import { ImageData, useTypedMediaItem } from '../media';
import { useAssetTags } from './useAssetTags';

type ChangeAssetImageProps = {
  workspaceAsset: WorkspaceAsset;
  mediaPicker: MediaPickerComponent;
  onClose: () => void;
  /** [optional] Custom trigger element for opening the media picker */
  customTrigger?: ReactNode;
};
export const ChangeAssetImage: React.FC<ChangeAssetImageProps> = (props) => {
  const asset = props.workspaceAsset?.asset;
  const { t } = useTranslation();
  const MediaPicker = props.mediaPicker;
  const { handleSet, handleUploadProgress } =
    useTypedMediaItem<ImageData>(undefined);
  const [uploading, setUploading] = useState(false);

  const { updateAsset } = useUpdateAsset({
    assetId: asset.id,
  });

  const { assetTags, isLoaded: tagsLoaded } = useAssetTags(asset.id);

  const setCoverImage = (image: Media) => {
    onSubmit(image.id);
  };
  const onSubmit = async (imageId: string) => {
    const input: any = {
      name: asset?.name ?? '',
      coverImageId: imageId,
      mediaIds: asset?.media?.map((media) => media.id),
      description: asset?.description ?? '',
      details: asset?.details ?? '',
      groupIds: props.workspaceAsset?.groups?.map((group) => group.id),
      hideReportAndNoteOptionsAnonymous:
        props.workspaceAsset?.anonymousOptions?.hideReportAndNote ?? false,
      id: asset.id,
    };

    if (tagsLoaded) {
      input.tagIds = assetTags.map((tag) => tag.id);
    }

    await updateAsset(
      {
        input: input,
      },
      {
        onSettled: async () => {
          props?.onClose?.();
        },
      }
    );
  };

  return (
    <View style={styles.buttonContainer}>
      <MediaPicker
        mode={PickerMode.CAMERA}
        setMedia={(media: ImageData) => handleSet(media, setCoverImage)}
        setProgress={handleUploadProgress}
        setUploading={setUploading}
        IsSquareCameraView={true}
        customTrigger={
          props.customTrigger ? (
            <>{props.customTrigger}</>
          ) : (
            <View style={styles.buttonChange}>
              <View style={styles.container}>
                {uploading ? (
                  <View style={styles.loadingContainer}>
                    <LoadingIndicator size={20} />
                  </View>
                ) : (
                  <>
                    <MaterialCommunityIcons
                      name="autorenew"
                      color={Colors.primaryDark}
                      size={20}
                      style={{ paddingRight: 6 }}
                    />
                    <Text style={styles.labelButtonChange}>
                      {t('assets.change')}
                    </Text>
                  </>
                )}
              </View>
            </View>
          )
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,

    ...Platform.select({
      web: {
        bottom: 4,
        height: 56,
      },
      default: {
        zIndex: 9,
      },
    }),
  },
  buttonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonChange: {
    alignSelf: 'center',
    zIndex: 9,
    position: 'absolute',
    backgroundColor: Colors.white,
    color: Colors.primaryDark,
    borderRadius: 8,
    borderColor: Colors.primaryDark,
    borderWidth: 2,
    width: 145,
    height: 56,
    ...Platform.select({
      web: {
        bottom: 40,
        height: 56,
      },
      default: {
        justifyContent: 'center',
        bottom: 35,
        left: 40,
      },
    }),
  },
  labelButtonChange: {
    color: Colors.primaryDark,
    fontSize: 20,
    fontWeight: 500,
  },
  loadingContainer: {
    backgroundColor: Colors.white,
    margin: 'auto',
  },
});
